import { expr, type PineBinaryOp, type PineExpr } from "./ast";
import type {
  ConditionGraph,
  ConditionLeaf,
  ConditionNode,
  ConditionOperator,
  ConditionSource,
} from "./conditions";

function sourceToExpr(source: ConditionSource): PineExpr {
  switch (source.kind) {
    case "price":
      return expr.id(source.series);
    case "ema":
      return expr.call("ta.ema", [expr.id("close"), expr.num(source.length)]);
    case "sma":
      return expr.call("ta.sma", [expr.id("close"), expr.num(source.length)]);
    case "rsi":
      return expr.call("ta.rsi", [expr.id("close"), expr.num(source.length)]);
    case "constant":
      return expr.num(source.value);
  }
}

const COMPARATOR_OPS: Partial<Record<ConditionOperator, PineBinaryOp>> = {
  greater_than: ">",
  less_than: "<",
  greater_or_equal: ">=",
  less_or_equal: "<=",
  equal: "==",
  not_equal: "!=",
};

function leafToExpr(leaf: ConditionLeaf): PineExpr {
  const lhs = sourceToExpr(leaf.lhs);
  const rhs = sourceToExpr(leaf.rhs);
  if (leaf.op === "crosses_above") {
    return expr.call("ta.crossover", [lhs, rhs]);
  }
  if (leaf.op === "crosses_below") {
    return expr.call("ta.crossunder", [lhs, rhs]);
  }
  const op = COMPARATOR_OPS[leaf.op];
  if (!op) {
    throw new Error(`Unsupported condition operator: ${leaf.op}`);
  }
  return expr.bin(op, lhs, rhs);
}

function nodeToExpr(node: ConditionNode): PineExpr {
  if (node.kind === "leaf") {
    return leafToExpr(node);
  }
  if (node.conditions.length === 0) {
    // Empty groups are validated out upstream, but be defensive at the
    // boundary - emit a literal `false` so the strategy is provably inert.
    return expr.bool(false);
  }
  if (node.conditions.length === 1) {
    return nodeToExpr(node.conditions[0]);
  }
  // Left-associative fold. Group with parens to keep mixed AND/OR readable.
  const op = node.logic;
  return node.conditions
    .map(nodeToExpr)
    .reduce((acc, current, index) => {
      if (index === 0) return current;
      return expr.bin(op, acc, current);
    });
}

export function conditionGraphToExpr(graph: ConditionGraph): PineExpr | null {
  if (!graph.root) return null;
  return nodeToExpr(graph.root);
}
